import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useSession } from '@/auth/session'
import { Button } from '@/components/ui/button'
import { IDLE_CHECK_INTERVAL_MS, IDLE_UPDATE_MS, isDeviceMode, isOutboxEmpty } from '@/pwa/deviceMode'
import { useUpdatePrompt } from '@/pwa/useUpdatePrompt'

/**
 * Is anything on the page mid-entry right now? Checked against
 * `document.activeElement` (the WHOLE page, not just this component's own
 * subtree) — device mode must not auto-accept while a user anywhere in the
 * app has a field focused, not merely while THIS banner happens to have
 * focus, since this banner never renders any focusable field of its own in
 * the first place.
 */
function hasFocusedInput(): boolean {
  const active = document.activeElement
  if (active === null) return false
  if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT') return true
  return active.getAttribute('contenteditable') === 'true'
}

/**
 * E2E ONLY, and gated twice: a build define (`vite.config.ts`, `false`
 * everywhere except `playwright.config.ts`'s own build) that merely PERMITS
 * a `sessionStorage` read, plus the key itself, which only the one spec that
 * measures a banner sets. Both are needed, so a default build folds the
 * whole thing away and no other spec ever meets a banner it did not ask for.
 */
declare const __FORCE_UPDATE_BANNER__: boolean | undefined
const FORCE_UPDATE_BANNER: boolean = typeof __FORCE_UPDATE_BANNER__ === 'boolean' ? __FORCE_UPDATE_BANNER__ : false

/**
 * The prompt-style update affordance (architecture §8; task 13's `sw.ts`
 * top comment) — and the ONE place in this app that decides whether
 * accepting a waiting update happens by a click or on its own.
 *
 * BROWSER MODE: renders a plain banner with a Reload button. Clicking it is
 * the ONLY thing that ever calls `accept()` here — never automatic, never
 * on a timer. This is the direct answer to architecture §8's "no silent
 * mid-form reloads": a human who cares gets asked, always.
 *
 * DEVICE MODE (review §5.6): renders NOTHING — an enrolled kiosk has no one
 * to show a banner to — and instead runs an idle timer that calls the
 * SAME `accept()` on its own, but ONLY once ALL THREE conditions this
 * review names are true at the moment the timer fires: the outbox is
 * empty (`deviceMode.ts`'s `isOutboxEmpty` — VACUOUS today, Task 17
 * builds the outbox this reads), no input anywhere on the page is
 * focused, and there has been no interaction for `IDLE_UPDATE_MS`. All
 * three, not any — each is checked independently inside
 * `attemptAutoAccept` below, and none is treated as implied by another.
 *
 * The idle check is a REPEATING interval (`IDLE_CHECK_INTERVAL_MS`) tested
 * against a `lastInteractionAt` timestamp, not a single `setTimeout` that
 * only re-arms on a fresh interaction event — review round 1, Important 1,
 * fixed here after being measured: the earlier single-`setTimeout` shape
 * left a BLOCKED attempt (a focused input, in the round-1 measurement)
 * permanently dead once it fired once and returned early — a device left
 * with a field focused for over an HOUR, then blurred with no further
 * touch at all, never got a second chance to auto-accept, because nothing
 * re-armed the timer without a NEW interaction. A repeating check has no
 * such dead end: every tick re-evaluates all three conditions from
 * scratch, so a block that clears between one tick and the next is caught
 * on the very next tick, with no interaction required to notice. It only
 * runs at all while `waiting && deviceMode` is true — a device with
 * nothing waiting, or a browser session, never starts one — and stops
 * itself the moment `attemptAutoAccept` actually succeeds (see
 * `clearInterval` inside it): once accepted, there is nothing left to
 * retry.
 */
export function UpdateBanner() {
  const { waiting, accept } = useUpdatePrompt()
  const { pathname } = useLocation()
  const { me } = useSession()
  // `me` is `undefined` only before sign-in resolves or while signed out —
  // `AppShell` mounts this component after both of those are already ruled
  // out (see that file's own render order), so this fallback is defensive,
  // not expected to matter in the real app; it never claims device mode
  // for a session whose kind genuinely isn't known yet.
  const deviceMode = isDeviceMode(pathname, me?.kind ?? 'staff')
  // Deliberately NOT folded into `waiting`: `waiting && deviceMode` is what
  // arms the auto-accept timer below, and a forced banner reaching that
  // condition would make this flag change a kiosk's RELOAD behaviour rather
  // than merely draw a bar. The render gate is its only consumer.
  const forced = FORCE_UPDATE_BANNER && sessionStorage.getItem('gf-e2e-force-update-banner') === 'true'

  useEffect(() => {
    if (!waiting || !deviceMode) return undefined

    // `Date.now()`, not a tick counter: fake timers (`vi.useFakeTimers()`)
    // fake `Date` alongside `setTimeout`/`setInterval` by default, so this
    // stays deterministic under test exactly like the timers themselves —
    // confirmed by this file's own covering test, which drives both with
    // the SAME fake clock.
    let lastInteractionAt = Date.now()
    function recordInteraction(): void {
      lastInteractionAt = Date.now()
    }

    function attemptAutoAccept(): void {
      if (Date.now() - lastInteractionAt < IDLE_UPDATE_MS) return
      if (!isOutboxEmpty()) return
      if (hasFocusedInput()) return
      accept()
      // Nothing left to retry once this succeeds — see this effect's own
      // top comment for why the interval stops itself here rather than
      // continuing to fire (harmlessly, but pointlessly) until unmount.
      clearInterval(intervalId)
    }

    // A representative, not exhaustive, set of "someone is here" signals —
    // wide enough to cover pointer, keyboard and touch interaction without
    // needing every possible event this app could ever emit.
    const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
    for (const type of INTERACTION_EVENTS) window.addEventListener(type, recordInteraction)

    const intervalId = setInterval(attemptAutoAccept, IDLE_CHECK_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
      for (const type of INTERACTION_EVENTS) window.removeEventListener(type, recordInteraction)
    }
  }, [waiting, deviceMode, accept])

  // `deviceMode` still wins over `forced` — an enrolled kiosk renders nothing
  // whatever the flag says.
  if ((!waiting && !forced) || deviceMode) return null

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-border bg-accent px-4 py-2 text-sm"
    >
      <p>A new version of GridFit is available.</p>
      <Button size="sm" onClick={accept}>
        Reload to update
      </Button>
    </div>
  )
}
